import { Analytics } from '@watershed/analytics/analyticsUtils';
import { useEffect } from 'react';
import PageContainer from '@watershed/shared-frontend/components/PageContainer';
import { Trans } from '@lingui/react/macro';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import {
  Error,
  FlexiError,
} from '@watershed/ui-core/components/shared/ErrorDisplayComponents';
import { ErrorCode } from '@watershed/errors/ErrorRegistry';

interface Props {
  title?: string | JSX.Element;
  subtitle?: string | JSX.Element;
}

export function NotFoundPageBody({
  title = (
    <Trans context="Title of the page when it is not found">
      Page not found
    </Trans>
  ),
  subtitle = (
    <Trans>
      The page you are looking for does not exist. It may have been moved or
      deleted.
    </Trans>
  ),
}: Props) {
  useEffect(() => {
    Analytics.error('notFoundPageShown');
  }, []);

  const notFoundError: FlexiError = {
    name: 'NotFoundError',
    message: 'Not Found',
    code: 'NOT_FOUND' satisfies ErrorCode,
  };

  return (
    <Error.Root testId={TestIds.NotFoundPageBody}>
      <Error.Icon error={notFoundError} testId={TestIds.NotFoundIcon} />
      <Error.Title align="center">{title}</Error.Title>
      {subtitle && (
        <Error.Description align="center">{subtitle}</Error.Description>
      )}
    </Error.Root>
  );
}

export default function NotFoundPage({ title, subtitle }: Props) {
  return (
    <PageContainer
      isFullPage={false}
      maxWidth="sm"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 64,
      }}
      data-testid={TestIds.NotFoundPage}
    >
      <NotFoundPageBody title={title} subtitle={subtitle} />
    </PageContainer>
  );
}

export function ViewerNoPermissionPage({ orgName }: { orgName: string }) {
  return (
    <NotFoundPage
      title={
        <Trans context="Unable to view page as in not authorized">
          Unable to view page
        </Trans>
      }
      subtitle={
        <Trans>
          You don’t have access to view this page. Reach out to an administrator
          at {orgName} to request access to the Watershed dashboard.
        </Trans>
      }
    />
  );
}
